import { useState } from "react";
import { ImagePlus, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

const field = "mt-1 w-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] rounded-lg px-3 py-2.5 text-sm text-[color:var(--text)] focus:outline-none focus:border-[color:var(--accent)] transition-colors";
const Field = ({ label, children }) => <label className="block text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}{children}</label>;
const MAX_MB = 8;

export const GalleryUploadForm = ({ courses, onClose, onUploaded }) => {
  const [form, setForm] = useState({ email: "", course_id: "", caption: "", website: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Choisis une image (JPEG, PNG, WebP)"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Image trop lourde (max ${MAX_MB} Mo)`); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Ajoute une photo"); return; }
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      const { data } = await api.post("/courses/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Photo envoyée, merci !");
      onUploaded?.(data);
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="glass rounded-2xl p-8 text-center space-y-3" data-testid="gallery-upload-success">
        <CheckCircle2 className="w-10 h-10 mx-auto text-[color:var(--accent)]" />
        <h3 className="font-display text-3xl">Photo bien reçue !</h3>
        <p className="text-sm text-[color:var(--text-muted)]">La régie la valide avant publication : elle apparaîtra dans la galerie sous peu.</p>
        <button type="button" onClick={onClose} data-testid="gallery-upload-close" className="text-xs uppercase tracking-widest text-[color:var(--accent)] underline">Fermer</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-4 relative" data-testid="gallery-upload-form">
      <button type="button" onClick={onClose} aria-label="Fermer" data-testid="gallery-upload-cancel" className="absolute top-4 right-4 text-[color:var(--text-muted)] hover:text-white"><X className="w-4 h-4" /></button>
      <div>
        <h3 className="font-display text-3xl">Partager une photo</h3>
        <p className="text-xs text-[color:var(--text-muted)] mt-1">Réservé aux familles inscrites : indique l'e-mail utilisé lors de l'inscription. Chaque photo est validée par la régie avant publication.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="E-mail de la famille *"><input data-testid="gallery-email" required type="email" value={form.email} onChange={set("email")} className={field} /></Field>
        <Field label="Cours (optionnel)">
          <select data-testid="gallery-course" value={form.course_id} onChange={set("course_id")} className={field}>
            <option value="">— celui de l'inscription —</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Légende (optionnel)"><input data-testid="gallery-caption" maxLength={200} value={form.caption} onChange={set("caption")} placeholder="Premier freeze réussi !" className={field} /></Field>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <label className="block rounded-xl border border-dashed border-[color:var(--border)] hover:border-[color:var(--accent)] transition-colors cursor-pointer overflow-hidden" data-testid="gallery-dropzone">
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" data-testid="gallery-file" />
        {preview
          ? <img src={preview} alt="Aperçu" className="w-full max-h-72 object-contain bg-black" />
          : <div className="p-8 text-center text-sm text-[color:var(--text-muted)] flex flex-col items-center gap-2"><ImagePlus className="w-6 h-6 text-[color:var(--accent)]" /> Choisir une photo (JPEG, PNG, WebP · max {MAX_MB} Mo)</div>}
      </label>
      <p className="text-[10px] text-[color:var(--text-muted)]">En envoyant, tu confirmes avoir le droit de partager cette image et acceptes sa publication sur la page des cours. Les métadonnées (lieu, appareil) sont supprimées automatiquement.</p>
      <button data-testid="gallery-submit" disabled={busy} className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Envoyer la photo
      </button>
    </form>
  );
};
